import React, { useMemo, useRef, useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import Header from "../components/header";
import "../css/pricing.css";

/**
 * ADAPT — Pricing (React) + EmailJS
 * Mantiene il layout/stile DESKTOP identico (inline) e aggiunge responsive via CSS.
 */

export default function AdaptPricing() {
  const formRef = useRef(null);
  const signupRef = useRef(null);
  const emailRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // opzionale: time-trap anti-bot (evita invii < 2s dall'apertura form)
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    let t = form.querySelector('input[type="hidden"][name="form_started_at"]');
    if (!t) {
      t = document.createElement("input");
      t.type = "hidden";
      t.name = "form_started_at";
      form.appendChild(t);
    }
    t.value = String(Date.now());
  }, []);

  const gotoForm = (plan) => {
    const form = formRef.current;
    if (!form) return;

    const boxes = form.querySelectorAll('input[name="interest"]');
    boxes.forEach((b) => {
      if (b.value === plan) b.checked = true;
    });

    try {
      signupRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {}

    setTimeout(() => emailRef.current?.focus(), 400);
  };

  // Ensure hidden field exists and set value (used to pass joined checkboxes + page_url)
  const ensureHidden = (name, value) => {
    const form = formRef.current;
    let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    const form = formRef.current;
    if (!form) return;

    // Collect multi-select checkbox values and join them to strings
    const getChecked = (name) =>
      Array.from(form.querySelectorAll(`input[name="${name}"]:checked`)).map((i) => i.value);

    ensureHidden("interest", getChecked("interest").join(", "));
    ensureHidden("usecase", getChecked("usecase").join(", "));
    ensureHidden("page_url", typeof window !== "undefined" ? window.location.href : "");

    // Honeypot anti-bot (invisible input named "_honey" kept empty)
    const botField = form.querySelector('input[name="_honey"]');
    if (botField && botField.value.trim().length > 0) {
      setLoading(false);
      setErrorMsg("Suspicious submission blocked.");
      return;
    }

    // time-trap: blocca invii ultra-rapidi (probabile bot)
    const startedAt = Number(form.querySelector('input[name="form_started_at"]')?.value || 0);
    if (startedAt && Date.now() - startedAt < 2000) {
      setLoading(false);
      setErrorMsg("Suspicious submission blocked.");
      return;
    }

    emailjs
      .sendForm(
        "service_hx03lau",           // <-- il tuo SERVICE ID
        "template_ltmbv9c",          // <-- il tuo TEMPLATE ID
        form,
        { publicKey: "nSnwnUQexEHBCUNt8" } // <-- la tua PUBLIC KEY
      )
      .then(() => {
        setSuccessMsg("Thanks! Your request has been recorded. We will contact you shortly.");
        form.reset();
      })
      .catch((err) => {
        console.error("EmailJS error:", err);
        setErrorMsg(err?.text || "Sending failed. Please try again later.");
      })
      .finally(() => setLoading(false));
  };

  // Token stile (DESKTOP) — restano inline per non toccare il layout attuale
  const cardStyle = useMemo(
    () => ({
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 5,
      padding: 15,
    }),
    []
  );

  const panelStyle = useMemo(
    () => ({
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      overflow: "hidden",
    }),
    []
  );

  const pillStyle = {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    color: "#334155",
    margin: "0 8px 8px 0",
  };

  const badgeStyle = {
    display: "inline-block",
    padding: "6px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 999,
    color: "#0583B3",
    fontWeight: 600,
    letterSpacing: ".2px",
  };

  const muted = { color: "#64748b" };

  const btnAltCls = "sign-button button";

  return (
    <>
      <Header />

      {/* WRAPPER SCORREVOLE */}
     
        <div
          className="adapt-container"
          style={{ padding: "0 12.5%", width: "75%", textAlign: "center", margin: "0 auto" }}
        >
          <header className="hero" style={{ padding: "75px 0 20px" }}>
            <span className="badge" style={badgeStyle}>ADAPT — Pricing</span>
            <h1
              className="hero-title"
              style={{ fontSize: 32, lineHeight: 1.2, margin: ".5rem 0 1rem", color: "#000088" }}
            >
              Start fast. Scale when ready.
            </h1>
            <p className="lead">
              Buy tokens to run any ADAPT workflow. Your workspace has capped
              storage and you can <strong>export your data at any time </strong>
              - after export, nothing remains on SoftMining servers.
            </p>

            {/* Cards */}
            <div
              className="cards-3"
              style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, margin: "20px 0 6px" }}
            >
              <div style={cardStyle}>
                <h3>Token-based</h3>
                <p>
                  Simple accounting across modules. Free utilities remain available
                  at no extra charge; paid workflows consume tokens.
                </p>
              </div>
              <div style={cardStyle}>
                <h3>Use all tools</h3>
                <p>
                  Run generative/predictive models, closed-loop design–make–test,
                  and utilities like <strong>VendorScout</strong> (SMILES→suppliers
                  & prices) and <strong>YADAMP Connector</strong> (public AMP
                  database).
                </p>
              </div>
              <div style={cardStyle}>
                <h3>Secure by default</h3>
                <p>
                  Private workspaces, selective sharing, audit-ready logs. Export
                  and purge in one click.
                </p>
              </div>
            </div>
          </header>

          {/* PLANS */}
          <section className="section" id="plans" style={{ margin: "75px 0"}}>
            <h2 className="sectionTitle" style={{ margin: "0 0 14px" }}>
              Plans
            </h2>

            <div role="region" aria-label="Pricing plans comparison" className="panel" style={panelStyle}>
              <div className="table-responsive">
                <table className="pricing-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {/* EMPTY LEADING HEADER CELL TO ALIGN PLAN COLUMNS AS 2 AND 3 */}
                      <th
                        aria-hidden="true"
                        className="th-empty"
                        style={{ padding: 18, background: "#f8fafc", borderRight: "1px solid #e5e7eb" }}
                      />
                      <th scope="col" style={{ padding: 18, fontSize: 18, background: "#f8fafc" }}>
                        <strong>Catalyst Starter</strong>
                        <small style={{ display: "block", fontWeight: 500, color: "#64748b" }}>
                          New users — token bundle
                        </small>
                      </th>
                      <th scope="col" style={{ padding: 18, fontSize: 18, background: "#f8fafc" }}>
                        <strong>Premium Lab</strong>
                        <small style={{ display: "block", fontWeight: 500, color: "#64748b" }}>
                          Scale up for active programs
                        </small>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* PRICES ROW WITH EMPTY LEADING CELL */}
                    <tr>
                      <td className="td-empty" style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb" }} />
                      <td
                        className="price"
                        style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb", fontWeight: 800, fontSize: 20 }}
                      >
                        €500 (~$600) for 1,000 tokens
                      </td>
                      <td
                        className="price"
                        style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb", fontWeight: 800, fontSize: 20 }}
                      >
                        1 token = €1 + 500&nbsp;GB storage / 12 months
                      </td>
                    </tr>

                    <tr style={{ background: "#fafafa" }}>
                      <th
                        scope="row"
                        className="rowhead"
                        style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb", verticalAlign: "top" }}
                      >
                        Platform access
                      </th>
                      <td style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb" }}>
                        All ADAPT tools, incl. VendorScout & YADAMP Connector
                      </td>
                      <td style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb" }}>
                        All ADAPT tools with higher quotas & concurrency
                      </td>
                    </tr>
                    <tr>
                      <th
                        scope="row"
                        className="rowhead"
                        style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb", verticalAlign: "top" }}
                      >
                        Storage
                      </th>
                      <td style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb" }}>
                        Capped workspace; secure export & purge
                      </td>
                      <td style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb" }}>
                        500&nbsp;GB included for 12 months; expansion available
                      </td>
                    </tr>
                    <tr style={{ background: "#fafafa" }}>
                      <th
                        scope="row"
                        className="rowhead"
                        style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb", verticalAlign: "top" }}
                      >
                        Support
                      </th>
                      <td style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb" }}>
                        Email support
                      </td>
                      <td style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb" }}>
                        Priority support & onboarding
                      </td>
                    </tr>
                    <tr>
                      <th
                        scope="row"
                        className="rowhead"
                        style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb", verticalAlign: "top" }}
                      >
                        Best for
                      </th>
                      <td style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb" }}>
                        Pilots and evaluation
                      </td>
                      <td style={{ padding: "16px 18px", borderTop: "1px solid #e5e7eb" }}>
                        Active R&amp;D programs and teams
                      </td>
                    </tr>

                    {/* CTA ROW WITH EMPTY LEADING CELL */}
                    <tr>
                      <td className="td-empty" style={{ padding: "22px 18px", borderTop: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb" }} />
                      <td style={{ padding: "22px 18px", borderTop: "1px solid #e5e7eb" }}>
                        <button className={btnAltCls} onClick={() => gotoForm("Catalyst Starter")}>
                          Get Catalyst Starter
                        </button>
                      </td>
                      <td style={{ padding: "22px 18px", borderTop: "1px solid #e5e7eb" }}>
                        <button className={btnAltCls} onClick={() => gotoForm("Premium Lab")}>
                          Upgrade to Premium
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="note" style={{ fontSize: 13, ...muted, marginTop: 8 }}>
                Prices exclude VAT. USD amount is indicative. Tokens are non-transferable
                and expire 12 months after purchase unless otherwise agreed.
              </p>
            </div>
          </section>

          {/* TOKENS LIST */}
          <section className="section" style={{ margin: "100px 0 20px 0" }}>
            <h2 className="sectionTitle" style={{ margin: "100px 0 20px 0" }}>
              What you can do with tokens
            </h2>
            <div
              className="grid-2"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
            >
              <div style={{ ...cardStyle, textAlign: "left" }}>
                <ul>
                  <li>Map disease networks and run multi-objective design</li>
                  <li>Launch generative & predictive models</li>
                  <li>Automate synthesis/assays in closed loops</li>
                </ul>
              </div>
              <div style={{ ...cardStyle, textAlign: "left" }}>
                <ul>
                  <li>
                    Use <strong>VendorScout</strong> to find suppliers/prices from SMILES
                  </li>
                  <li>
                    Query the <strong>YADAMP Connector</strong> for AMP sequences
                  </li>
                  <li>Export all results safely; nothing retained after export</li>
                </ul>
              </div>
            </div>
          </section>

          {/* COMING SOON */}
          <section className="section" style={{ margin: "100px 0" }}>
            <h2 className="sectionTitle" style={{ margin: "0 0 14px" }}>
              Coming soon
            </h2>
            <p className="muted" style={muted}>
              Target identification, drug repurposing, toxicity prediction, and
              binding-energy evaluation modules. New packages will be added
              continuously—stay tuned.
            </p>
            <div className="pills">
              <span style={pillStyle}>Target ID</span>
              <span style={pillStyle}>Repurposing</span>
              <span style={pillStyle}>Toxicity</span>
              <span style={pillStyle}>Binding Energy</span>
            </div>
          </section>

          {/* SIGNUP / ONBOARDING */}
          <section className="section" id="signup" ref={signupRef} style={{ margin: "100px 0" }}>
            <h2 className="sectionTitle" style={{ margin: "0 0 14px" }}>
              Join the list / Request onboarding
            </h2>

            <form
              ref={formRef}
              onSubmit={handleSubmit}
              autoComplete="off"
              className="formRootPricing"
              style={{ ...cardStyle }}
            >
              {/* Honeypot: must stay empty and hidden for real users */}
              <input
                type="text"
                name="_honey"
                autoComplete="new-password"
                tabIndex="-1"
                className="honey"
                style={{ display: "none" }}
                aria-hidden="true"
                inputMode="none"
                enterKeyHint="done"
              />

              <label className="label" htmlFor="email">
                Work email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="inputText"
                placeholder="name@company.com"
                required
                ref={emailRef}
              />

              <div className="space"></div>

              <label className="label" htmlFor="org">
                Organization / Role
              </label>
              <input
                id="org"
                name="org"
                type="text"
                className="inputText"
                placeholder="Company, Department — Role"
              />

              <div className="space"></div>

              <label className="label">I'm interested in</label>
              <div className="optline" style={{ display: "flex", flexWrap: "wrap", gap: 10, margin: "10px 0 0px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 5, margin: 0 }}>
                  <input type="checkbox" name="interest" value="More information" /> More
                  information
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 5, margin: 0 }}>
                  <input type="checkbox" name="interest" value="Catalyst Starter" /> Catalyst
                  Starter
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 5, margin: 0 }}>
                  <input type="checkbox" name="interest" value="Premium Lab" /> Premium Lab
                </label>
              </div>

              <div className="space"></div>

              <label className="label">Use case</label>
              <div className="optline" style={{ display: "flex", flexWrap: "wrap", gap: 10, margin: "10px 0 0px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                  <input type="checkbox" name="usecase" value="Drug discovery" /> Drug discovery
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                  <input type="checkbox" name="usecase" value="Biomaterials" /> Biomaterials
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                  <input type="checkbox" name="usecase" value="Both" /> Both
                </label>
              </div>

              <div className="space"></div>

              <button className={`${btnAltCls} primary`} type="submit" disabled={loading}>
                {loading ? "Sending..." : "Subscribe / Request access"}
              </button>
              <p className="note" style={{ fontSize: 13, ...muted }}>
                We’ll email payment and setup instructions. By submitting, you agree to be
                contacted about ADAPT. You can opt out anytime.
              </p>

              {errorMsg && (
                <div
                  role="alert"
                  className="alert error"
                  style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }}
                >
                  {errorMsg}
                </div>
              )}

              {successMsg && (
                <div
                  className="alert success"
                  style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" }}
                >
                  {successMsg}
                </div>
              )}
            </form>
          </section>

          {/* FOOTER */}
          <footer className="footer" style={{ marginTop: "-25px", ...muted, fontSize: 13 }}>
            © SoftMining — ADAPT. Security-first: private workspaces, export-and-purge,
            audit-ready logs.
          </footer>
        </div>
    </>
  );
}
